import { translateKey } from 'src/locales/translate';
// @mui

import {
  Box,
  Dialog,
  DialogProps,
  DialogTitle,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { visitApi } from 'src/api/visit';
import { useEffect, useMemo, useState } from 'react';
import { conversationApi } from 'src/api/conversation';
import Label from 'src/components/label';
import ServiceBadge from 'src/components/service-badge';
import VisitPaymentStatusBadge from 'src/components/visit-payment-status-badge/VisitPaymentStatusBadge';
import VisitStatusBadge from 'src/components/visit-status-badge/VisitStatusBadge';
import VisitTypeBadge from 'src/components/visit-type-badge/VisitTypeBadge';
import VisitWalkInBadge from 'src/components/visit-walk-in-badge/VisitWalkInBadge';
import usePrevious from 'src/hooks/usePrevious';
import { useResponsiveBreakpoints } from 'src/hooks/useResponsiveBreakpoints';
import {
  GetVisitById,
  GetVisitByIdStatusEnum,
  GetVisitByIdVisitTypeEnum,
} from 'woofwoof-api';
import { Roles } from 'src/types/User';
import { isVisitEditWindowOpen } from 'src/utils/visitEditWindow';
import { useOrganizationContext } from '../../hooks/useOrganizationContext';
import OrganizationVisitModalAssistant from './OrganizationVisitModalAssistant';
import OrganizationVisitModalSkeleton from './OrganizationVisitModalSkeleton';
import OrganizationVisitModalTabs from './OrganizationVisitModalTabs';
import { VisitModalActionProvider } from './VisitModalActionContext';
import { VisitAssistantActionProvider } from './VisitAssistantActionContext';
import {
  VISIT_MODAL_ASSISTANT_QUERY,
  VISIT_MODAL_PAPER_MEDIA,
  VISIT_MODAL_PAPER_QUERY,
} from './visitModalLayout';
import {
  resolveVisitDetails,
  visitDetailsKey,
} from 'src/sections/@dashboard/visit/utils/visitDetailsQuery';
import { isVisitAssistantEligible } from './assistantEligibility';

// ---------------------------------------------------------------------

type VisitModalProps = {
  isOpen: boolean;
  onClose: () => void;
  visit?: GetVisitById;
  onReschedule?: ({
    professionalId,
    date,
    visitId,
  }: {
    professionalId: string;
    date: string;
    visitId: string;
  }) => void;
  isRescheduleLoading?: boolean;
  onCancel?: () => void;
  isCancelLoading?: boolean;
  editOnly?: boolean;
  /** The caller is still reading `visit`: shows the skeleton until it lands. */
  loading?: boolean;
  /**
   * The caller already read `visit` by id (with its draft), so the modal does
   * not read it again. Invalidating `visitDetailsKey(id)` refreshes the
   * caller's query, whose key starts with it.
   */
  detailsFetched?: boolean;
};

export default function OrganizationVisitModal({
  isOpen,
  onClose,
  visit,
  onReschedule,
  isRescheduleLoading,
  onCancel,
  isCancelLoading,
  editOnly,
  loading = false,
  detailsFetched = false,
}: VisitModalProps) {
  const prevIsOpen = usePrevious(isOpen);
  const [lastVisit, setLastVisit] = useState<GetVisitById | undefined>(visit);
  const { selectedOrganization, selectedRole } = useOrganizationContext();
  const { isMd } = useResponsiveBreakpoints();
  // Fixed-height "paper" layout only when the viewport can actually fit the
  // 750px-min modal; otherwise fall back to body-scroll (see visitModalLayout).
  const isPaperScroll = useMediaQuery(VISIT_MODAL_PAPER_QUERY);
  const assistantFits = useMediaQuery(VISIT_MODAL_ASSISTANT_QUERY);

  // Read the visit by id rather than trusting the row we were handed: the list
  // endpoints omit `notesDraft` on purpose (see visitDetailsQuery), so an unsent
  // draft only reaches the modal through this fetch.
  const { data: visitDetails } = useQuery(
    visitDetailsKey(visit?.id),
    () => visitApi.visitControllerGetVisitById({ visitId: visit!.id }),
    { enabled: isOpen && !!visit?.id && !detailsFetched, retry: 1 },
  );

  // Resolved while rendering, so the render the visit lands in already shows
  // it; `lastVisit` keeps it on screen while the modal fades out.
  const openVisit = useMemo(
    () =>
      visit && isOpen
        ? resolveVisitDetails(visit, visitDetails?.data)
        : undefined,
    [visit, visitDetails, isOpen],
  );

  useEffect(() => {
    if (openVisit) {
      setLastVisit(openVisit);
    }
  }, [openVisit]);

  // While the caller reads the next visit, the last one (kept for the fade-out)
  // must not stand in for it: one modal serves every visit of the calendar.
  const visitCopy = openVisit ?? (loading ? undefined : lastVisit);

  const isVisitTypeVideo =
    visitCopy?.visitType === GetVisitByIdVisitTypeEnum.Video;
  const isVisitCompleted =
    visitCopy?.status === GetVisitByIdStatusEnum.Completed;
  // Treat ABORTED visits as cancelled for UI purposes — both terminal.
  const isVisitCancelled =
    visitCopy?.status === GetVisitByIdStatusEnum.Cancelled ||
    visitCopy?.status === GetVisitByIdStatusEnum.Aborted;
  const isVisitPending = visitCopy?.status === GetVisitByIdStatusEnum.Pending;

  // Show the Asystent Vetsi side panel for a completed OR confirmed-upcoming visit
  // while it's still within the 30-day edit window, to a professional in the
  // visit's own org, and only when the viewport can fit the panel beside the
  // content. The assistant is professional-only — the organization-admin role
  // never sees it. The backend additionally gates assistant access to the
  // caller's active org.
  const isSameOrg =
    !!selectedOrganization?.id &&
    selectedOrganization.id === visitCopy?.organization?.id;
  const canUseAssistant = selectedRole === Roles.professional;
  const showAssistant =
    !!visitCopy &&
    isVisitAssistantEligible(visitCopy.status) &&
    canUseAssistant &&
    isSameOrg &&
    isVisitEditWindowOpen(visitCopy.status, visitCopy.endTime) &&
    assistantFits;

  const { isLoading: isConversationLoading, remove: removeConversation } =
    useQuery(
      ['conversationDetailsModal'],
      () =>
        conversationApi.conversationControllerGetConversationById({
          conversationId: (visitCopy?.conversation as any)?.id,
        }),
      {
        retry: 1,
        enabled: isVisitCompleted || isVisitCancelled,
      },
    );

  useEffect(() => {
    if (!isOpen && prevIsOpen) {
      removeConversation();

      setTimeout(() => {
        setLastVisit(undefined);
      }, 150);
    }
  }, [removeConversation, isOpen, prevIsOpen]);

  // The visit tabs/content — shared between the single-column layout and the
  // left column of the two-column (assistant) layout.
  const tabsContent = (
    <VisitModalActionProvider>
      <OrganizationVisitModalTabs
        visit={visitCopy}
        isConversationLoading={isConversationLoading}
        onClose={onClose}
        isOpen={isOpen}
        onReschedule={onReschedule}
        isRescheduleLoading={isRescheduleLoading}
        onCancel={onCancel}
        isCancelLoading={isCancelLoading}
        editOnly={editOnly}
      />
    </VisitModalActionProvider>
  );

  const dialogProps: DialogProps = {
    open: isOpen,
    onClose,
    // Fixed-height "paper" layout (pinned header/footer, body scrolls
    // internally) only when the viewport can fit the 750px-min modal;
    // otherwise body-scroll so the title/footer never get clipped on short or
    // narrow screens.
    scroll: isPaperScroll ? 'paper' : 'body',
    PaperProps: {
      sx: {
        width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 48px)' },
        // Wider when the assistant panel sits beside the content.
        maxWidth: showAssistant ? '1560px' : '900px',
        mx: 0,
        my: { xs: 2, sm: 4 },
        // Desktop width always gets at least a 750px-tall modal. When the
        // viewport is also tall enough (paper mode) it renders at a fixed full
        // height — 1050px, capped to the viewport with ~32px breathing room —
        // and the body scrolls internally. On short/narrow viewports only the
        // 750px floor applies and the whole modal scrolls with the page.
        minHeight: { md: 750 },
        [VISIT_MODAL_PAPER_MEDIA]: {
          maxHeight: 'calc(100vh - 64px)',
          height: 'min(1050px, calc(100vh - 64px))',
        },
      },
    },
  };

  // The same Dialog as below, so the skeleton gives way to the visit in place.
  if (loading && !visitCopy) {
    return (
      <Dialog {...dialogProps} data-testid="organization-visit-modal">
        <OrganizationVisitModalSkeleton onClose={onClose} />
      </Dialog>
    );
  }

  return (
    <Dialog {...dialogProps} data-testid="organization-visit-modal">
      <DialogTitle
        component="div"
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flex: 1,
          }}
        >
          <Stack>
            <Typography variant="h6">
              {visitCopy?.startTime &&
                format(new Date(visitCopy.startTime), 'dd/MM/yyyy H:mm')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {visitCopy?.organization?.name}
            </Typography>
            {visitCopy?.visitAddress && (
              <Stack>
                <Label
                  variant="soft"
                  color="success"
                  sx={{ textTransform: 'none', width: 'fit-content', mt: 1 }}
                >
                  <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                    <Typography
                      sx={{ color: 'inherit', font: 'inherit' }}
                      component="span"
                    >
                      {translateKey('visit.visitAddressLabelColon')}
                    </Typography>
                    <Link
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        `${visitCopy?.visitAddress?.street}, ${visitCopy?.visitAddress?.zipCode} ${visitCopy?.visitAddress?.city}`,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: 'underline',
                        color: 'inherit',
                      }}
                    >
                      {`${visitCopy?.visitAddress?.street}, ${visitCopy?.visitAddress?.zipCode} ${visitCopy?.visitAddress?.city}`}
                    </Link>
                  </Stack>
                </Label>
              </Stack>
            )}
          </Stack>

          <Stack sx={{ gap: 1 }}>
            <Stack
              sx={{
                flexDirection: isMd ? 'column' : 'row',
                gap: 1,
                justifyContent: isMd ? 'flex-start' : 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              {visitCopy?.visitType && (
                <VisitTypeBadge visitType={visitCopy.visitType} />
              )}
              <VisitWalkInBadge origin={visitCopy?.origin} />
              {visitCopy?.service && (
                <ServiceBadge
                  serviceName={visitCopy.service.name}
                  duration={visitCopy.service.duration}
                  price={visitCopy.service.price}
                  visitType={visitCopy.visitType}
                  showPrice={
                    selectedOrganization?.id === visitCopy.organization?.id
                  }
                />
              )}
            </Stack>
            <Stack
              sx={{
                justifyContent: isMd ? 'flex-start' : 'flex-end',
                flexDirection: isMd ? 'column' : 'row',
              }}
            >
              {isVisitTypeVideo ? (
                isVisitCompleted || isVisitCancelled || isVisitPending ? (
                  <VisitStatusBadge
                    visitStatus={visitCopy?.status}
                    rejectedBy={visitCopy?.rejectedBy}
                  />
                ) : (
                  <VisitPaymentStatusBadge
                    paymentStatus={visitCopy.payment?.status}
                  />
                )
              ) : (
                <VisitStatusBadge
                  visitStatus={visitCopy?.status}
                  rejectedBy={visitCopy?.rejectedBy}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </DialogTitle>
      <VisitAssistantActionProvider>
        {showAssistant && visitCopy ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              minHeight: 0,
              width: '100%',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {tabsContent}
            </Box>
            <Box
              sx={{
                width: 600,
                flexShrink: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderLeft: '1px solid',
                borderColor: 'divider',
                p: 3,
              }}
            >
              <OrganizationVisitModalAssistant
                visit={visitCopy}
                edgeScrollbar
              />
            </Box>
          </Box>
        ) : (
          tabsContent
        )}
      </VisitAssistantActionProvider>
    </Dialog>
  );
}
